import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../components/Flex";
import { GlobalTableHeightWrapper } from "../../../../../../../components/GlobalTableHeightWrapper";
import StatusChip from "../../../../../../../components/StatusChip";
import Text from "../../../../../../../components/Text";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../components/app/DeleteModal";
import { DSCheckbox } from "../../../../../../../components/ds/DSCheckbox";
import { DSTag } from "../../../../../../../components/ds/DSTag";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useGetPatientCards } from "../../../../../../../queries/billings/payments/cards/use-patient-cards";
import { useArchivePatientCard } from "../../../../../../../queries/billings/payments/use-archive-patient-card";
import { useSetPrimaryCard } from "../../../../../../../queries/billings/payments/use-set-primary-card";
import { PatientCard } from "../../../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../../../utils/utils";
import EditCardModal from "./EditCardModal";

export const CARD_BRAND_ICONS: Record<string, string> = {
  visa: "https://img.icons8.com/color/48/000000/visa.png",
  mastercard: "https://img.icons8.com/color/48/000000/mastercard-logo.png",
  amex: "https://img.icons8.com/color/48/000000/amex.png",
  diners: "https://img.icons8.com/color/48/000000/diners-club.png",
  diners_club: "https://img.icons8.com/color/48/000000/diners-club.png",
  discover: "https://img.icons8.com/color/48/000000/discover.png",
  jcb: "https://img.icons8.com/color/48/000000/jcb.png",
  unionpay: "https://img.icons8.com/color/48/000000/unionpay.png",
  rupay: "https://seeklogo.com/images/R/rupay-logo-CA2C0C6DCB-seeklogo.com.png",
};

// Compact override for the "Primary" tag inside the card grid row. DSTag's
// default sizing is built for standalone tags and overpowers a table row, so
// the padding and label are scaled down here rather than in the shared atom.
const PRIMARY_TAG_SX = {
  marginLeft: "8px",
  px: "6px",
  py: "0px",
  // The grid cell clips its overflow, so the tag must never be the element
  // that gives way when the column is narrow.
  flexShrink: 0,
  "& .ds-tag-text": {
    fontSize: "10px",
    lineHeight: "12px",
  },
};

// Display string for a card's expiry. Prefer the BE-provided `cardExpiry`,
// otherwise compose it from the numeric month/year parts.
const getCardExpiry = (row: any): string => {
  if (row?.cardExpiry) return row.cardExpiry;
  if (row?.expMonth && row?.expYear) {
    return `${String(row.expMonth).padStart(2, "0")}/${row.expYear}`;
  }
  return "-";
};

// A card is expired once its expiry month/year is in the past. Falls back to
// parsing the BE-provided "MM/YY" / "MM/YYYY" cardExpiry string.
const isCardExpired = (row: any): boolean => {
  let expMonth = row?.expMonth;
  let expYear = row?.expYear;
  if ((!expMonth || !expYear) && row?.cardExpiry) {
    const [mm, yy] = String(row.cardExpiry)
      .split("/")
      .map((part) => part.trim());
    expMonth = Number(mm);
    expYear = yy?.length === 2 ? 2000 + Number(yy) : Number(yy);
  }
  if (!expMonth || !expYear) return false;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  return (
    expYear < currentYear ||
    (expYear === currentYear && expMonth < currentMonth)
  );
};

const PatientCardDetails = () => {
  const { hasReadOnly } = useRoleController("Financials");
  const patientId = useParams()?.patientId ?? "";
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const editCardDisclosure = useDisclosure(false);
  const [selectedCard, setSelectedCard] = useState<PatientCard | undefined>();
  // Archived (deleted) cards are filtered by the backend via the `archive`
  // param. Unchecked = active cards only; checked = archived cards.
  const [showArchived, setShowArchived] = useState<boolean>(false);

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const { mutateAsync: setPrimaryCard } = useSetPrimaryCard();
  const { mutateAsync: archivePatientCard } = useArchivePatientCard();

  const {
    data: patientCardDetails,
    isFetching,
    isLoading,
  } = useGetPatientCards({
    pagination: pagination,
    typeOfPayment: "PATIENT",
    paymentMode: "ONLINE",
    archived: showArchived,
    patientUuid: patientId,
  });

  const archiveCardDisclosure = useDeleteDisClosure();

  const invalidateCards = () => {
    queryClient.invalidateQueries({ queryKey: ["get-patient-payment-v2"] });
    queryClient.invalidateQueries({ queryKey: ["get-patient-cards"] });
  };

  function handleMarkPrimary(row: any) {
    setPrimaryCard({ cardUuid: row?.uuid, xTENANTID: getXtenantId() })
      .then((res) => {
        invalidateCards();
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  // DeleteModal surfaces the success/error snackbar from the returned promise,
  // so just run the mutation and refresh the list here.
  function handleArchive(id: unknown) {
    return archivePatientCard({
      patientCardId: id?.toString() ?? "",
      status: true,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      invalidateCards();
      return res;
    });
  }

  function handleUnarchive(row: any) {
    archivePatientCard({
      patientCardId: row?.uuid ?? "",
      status: false,
      xTENANTID: getXtenantId(),
    })
      .then((res) => {
        invalidateCards();
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "srNo",
      flex: 0.2,
      minWidth: 50,
      headerName: "Sr. No",
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "displayBrand",
      flex: 0.5,
      minWidth: 300,
      headerName: "Card Number",
      renderCell: ({ row }) => {
        const brand = row.displayBrand?.toLowerCase() || "unknown";
        const brandIcon = CARD_BRAND_ICONS[brand];
        return (
          <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {brandIcon ? (
              <img
                width={30}
                src={brandIcon}
                alt={row.displayBrand || "card"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Text isTabletext style={{ color: "#74797B" }}>
                {replaceUnderScoreAddSpace(row.displayBrand)}
              </Text>
            )}
            <Box style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {row.last4 && (
                <>
                  <Text
                    isTabletext
                    style={{
                      color: "#74797B",
                      letterSpacing: "1px",
                    }}
                  >
                    •••• •••• ••••
                  </Text>
                  <Text
                    isTabletext
                    style={{
                      color: "#74797B",
                      letterSpacing: "1px",
                    }}
                  >
                    {row.last4}
                  </Text>
                </>
              )}
              {row.primary && (
                <DSTag label="Primary" active sx={PRIMARY_TAG_SX} />
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "cardHolderName",
      flex: 0.5,
      minWidth: 180,
      headerName: "Card Holder Name",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: "#373D41" }}>
          {row?.cardHolderName || "-"}
        </Text>
      ),
    },
    {
      field: "cardExpiry",
      flex: 0.4,
      minWidth: 130,
      headerName: "Expiration Date",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: "#373D41" }}>
          {getCardExpiry(row)}
        </Text>
      ),
    },
    {
      field: "isPrimary",
      flex: 0.4,
      minWidth: 100,
      headerName: "Primary",
      renderCell: ({ row }) => (
        <Text isTabletext style={{ color: "#373D41" }}>
          {row.primary ? "Yes" : "No"}
        </Text>
      ),
    },
    {
      field: "active",
      flex: 0.4,
      minWidth: 100,
      headerName: "Status",
      renderCell: ({ row }) => (
        <StatusChip
          value={replaceUnderScoreAddSpace(row?.cardStatus?.toLowerCase())}
        />
      ),
    },
    {
      field: "action",
      flex: 0.3,
      minWidth: 80,
      headerName: "Action",
      sortable: false,
      renderCell: ({ row }) => {
        const expired = isCardExpired(row);
        const archived = !!row?.archive;
        return (
          <ActionMenu
            options={
              archived
                ? [{ value: "unarchive", label: "Unarchive" }]
                : [
                    { value: "edit", label: "Edit" },
                    {
                      value: "setPrimary",
                      label: "Mark As Primary",
                      isDisabled: row.primary || expired,
                      hoverMsg: row.primary
                        ? "This card is already primary"
                        : "Expired cards cannot be marked as primary",
                    },
                    {
                      value: "archive",
                      label: "Archive",
                      isDisabled: row.primary,
                      hoverMsg:
                        "Remove the primary tag from this card before archiving it",
                    },
                  ]
            }
            onActionClick={(v) => {
              if (v === "edit") {
                setSelectedCard(row);
                editCardDisclosure.open();
              } else if (v === "archive") {
                archiveCardDisclosure.open(row?.uuid);
              } else if (v === "setPrimary") {
                handleMarkPrimary(row);
              } else if (v === "unarchive") {
                handleUnarchive(row);
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <EditCardModal disclosureHost={editCardDisclosure} card={selectedCard} />

      <DeleteModal
        w="380px"
        variant="warning"
        open={archiveCardDisclosure.opened}
        onClose={archiveCardDisclosure.close}
        id={archiveCardDisclosure.deleteId}
        onConfirmation={(id) => handleArchive(id)}
        content={"You want to archive this Patient Card Details"}
      />

      <Flex justifyContent={"flex-end"} mb={1}>
        <DSCheckbox
          label="Show Archived Cards"
          checked={showArchived}
          onChange={(e) => {
            setShowArchived(e.target.checked);
            setPagination(DEF_PAGINATION);
          }}
        />
      </Flex>

      <GlobalTableHeightWrapper marginTop={0}>
        <CustomDataGridV2
          getRowId={(row) => row?.uuid}
          rows={patientCardDetails?.data?.content ?? []}
          columns={filterActionCol(COLUMNS, hasReadOnly)}
          disableRowSelectionOnClick
          rowCount={patientCardDetails?.data?.page?.totalElements ?? 10}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          loading={isLoading || isFetching}
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default PatientCardDetails;
